import api, { apiMultipart } from './api';
import { 
  Anuncio, 
  AnuncioFormData, 
  FiltrosAnuncio, 
  PaginatedResponse,
  Favorito,
  Conversa,
  Mensagem,
  DashboardStats,
  AdminStats
} from '../types';

export const anunciosService = {
  // Anúncios
  async listar(filtros?: FiltrosAnuncio): Promise<PaginatedResponse<Anuncio>> {
    const params = new URLSearchParams();
    
    if (filtros) {
      if (filtros.busca) params.append('busca', filtros.busca);
      if (filtros.categoria) params.append('categoria', filtros.categoria);
      if (filtros.subcategoria) params.append('subcategoria', filtros.subcategoria);
      if (filtros.precoMin) params.append('precoMin', filtros.precoMin.toString());
      if (filtros.precoMax) params.append('precoMax', filtros.precoMax.toString());
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.cidade) params.append('cidade', filtros.cidade);
      if (filtros.ordenar) params.append('ordenar', filtros.ordenar);
      if (filtros.pagina) params.append('pagina', filtros.pagina.toString());
      if (filtros.limite) params.append('limite', filtros.limite.toString());
    }

    const response = await api.get<PaginatedResponse<Anuncio>>(`/anuncios?${params}`);
    return response.data;
  },

  async buscar(id: string): Promise<Anuncio> {
    const response = await api.get<Anuncio>(`/anuncios/${id}`);
    return response.data;
  },

  async criar(data: AnuncioFormData): Promise<Anuncio> {
    const formData = new FormData();
    
    formData.append('titulo', data.titulo);
    formData.append('descricao', data.descricao);
    formData.append('preco', data.preco.toString());
    formData.append('precoNegociavel', data.precoNegociavel.toString());
    formData.append('categoria', data.categoria);
    formData.append('subcategoria', data.subcategoria);
    formData.append('cidade', data.cidade);
    formData.append('estado', data.estado);
    formData.append('cep', data.cep);
    formData.append('caracteristicas', JSON.stringify(data.caracteristicas));
    
    data.imagens.forEach((imagem) => {
      formData.append('imagens', imagem);
    });

    const response = await apiMultipart.post<Anuncio>('/anuncios', formData);
    return response.data;
  },

  async atualizar(id: string, data: Partial<AnuncioFormData>): Promise<Anuncio> {
    const formData = new FormData();
    
    if (data.titulo) formData.append('titulo', data.titulo);
    if (data.descricao) formData.append('descricao', data.descricao);
    if (data.preco !== undefined) formData.append('preco', data.preco.toString());
    if (data.precoNegociavel !== undefined) formData.append('precoNegociavel', data.precoNegociavel.toString());
    if (data.categoria) formData.append('categoria', data.categoria);
    if (data.subcategoria) formData.append('subcategoria', data.subcategoria);
    if (data.cidade) formData.append('cidade', data.cidade);
    if (data.estado) formData.append('estado', data.estado);
    if (data.cep) formData.append('cep', data.cep);
    if (data.caracteristicas) formData.append('caracteristicas', JSON.stringify(data.caracteristicas));
    
    if (data.imagens) {
      data.imagens.forEach((imagem) => {
        formData.append('imagens', imagem);
      });
    }

    const response = await apiMultipart.put<Anuncio>(`/anuncios/${id}`, formData);
    return response.data;
  },

  async deletar(id: string): Promise<void> {
    await api.delete(`/anuncios/${id}`);
  },

  async pausar(id: string): Promise<Anuncio> {
    const response = await api.patch<Anuncio>(`/anuncios/${id}/pausar`);
    return response.data;
  },

  async ativar(id: string): Promise<Anuncio> {
    const response = await api.patch<Anuncio>(`/anuncios/${id}/ativar`);
    return response.data;
  },

  async marcarVendido(id: string): Promise<Anuncio> {
    const response = await api.patch<Anuncio>(`/anuncios/${id}/vendido`);
    return response.data;
  },

  async meusAnuncios(): Promise<Anuncio[]> {
    const response = await api.get<Anuncio[]>('/anuncios/meus');
    return response.data;
  },

  async destaques(): Promise<Anuncio[]> {
    const response = await api.get<Anuncio[]>('/anuncios/destaques');
    return response.data;
  },

  async recentes(limite: number = 8): Promise<Anuncio[]> {
    const response = await api.get<Anuncio[]>(`/anuncios/recentes?limite=${limite}`);
    return response.data;
  },

  // Favoritos
  async listarFavoritos(): Promise<Favorito[]> {
    const response = await api.get<Favorito[]>('/favoritos');
    return response.data;
  },

  async adicionarFavorito(anuncioId: string): Promise<Favorito> {
    const response = await api.post<Favorito>('/favoritos', { anuncioId });
    return response.data;
  },

  async removerFavorito(anuncioId: string): Promise<void> {
    await api.delete(`/favoritos/${anuncioId}`);
  },

  async isFavorito(anuncioId: string): Promise<boolean> {
    try {
      const response = await api.get<{ favorito: boolean }>(`/favoritos/check/${anuncioId}`);
      return response.data.favorito;
    } catch {
      return false;
    }
  },

  // Chat/Mensagens
  async listarConversas(): Promise<Conversa[]> {
    const response = await api.get<Conversa[]>('/conversas');
    return response.data;
  },

  async buscarConversa(id: string): Promise<Conversa> {
    const response = await api.get<Conversa>(`/conversas/${id}`);
    return response.data;
  },

  async iniciarConversa(anuncioId: string, mensagem: string): Promise<Conversa> {
    const response = await api.post<Conversa>('/conversas', { anuncioId, mensagem });
    return response.data;
  },

  async listarMensagens(conversaId: string): Promise<Mensagem[]> {
    const response = await api.get<Mensagem[]>(`/conversas/${conversaId}/mensagens`);
    return response.data;
  },

  async enviarMensagem(conversaId: string, conteudo: string): Promise<Mensagem> {
    const response = await api.post<Mensagem>(`/conversas/${conversaId}/mensagens`, { conteudo });
    return response.data;
  },

  async enviarOferta(conversaId: string, valor: number): Promise<Mensagem> {
    const response = await api.post<Mensagem>(`/conversas/${conversaId}/oferta`, { valor });
    return response.data;
  },

  async responderOferta(mensagemId: string, aceitar: boolean): Promise<Mensagem> {
    const response = await api.patch<Mensagem>(`/mensagens/${mensagemId}/oferta`, { aceitar });
    return response.data;
  },

  async marcarMensagensLidas(conversaId: string): Promise<void> {
    await api.patch(`/conversas/${conversaId}/lidas`);
  },

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>('/dashboard/stats');
    return response.data;
  },

  // Admin
  async getAdminStats(): Promise<AdminStats> {
    const response = await api.get<AdminStats>('/admin/stats');
    return response.data;
  },

  async listarAnunciosPendentes(): Promise<Anuncio[]> {
    const response = await api.get<Anuncio[]>('/admin/anuncios/pendentes');
    return response.data;
  },

  async aprovarAnuncio(id: string): Promise<Anuncio> {
    const response = await api.patch<Anuncio>(`/admin/anuncios/${id}/aprovar`);
    return response.data;
  },

  async rejeitarAnuncio(id: string, motivo: string): Promise<Anuncio> {
    const response = await api.patch<Anuncio>(`/admin/anuncios/${id}/rejeitar`, { motivo });
    return response.data;
  },
};

export default anunciosService;
